import { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useDraggable } from '@dnd-kit/core'
import { OwnerMark } from '@/components/Events'
import { eventTone, whoseCalendar } from '@/components/eventTone'
import { StatusRing } from '@/components/StatusRing'
import type { ShownEvent } from '@/hooks/useEvents'
import type { People } from '@/hooks/usePeople'
import { useTaskActions } from '@/hooks/useTaskActions'
import { fmtDate, minutesOf, pad2 } from '@/logic/dates'
import { gridHours, layoutSpans, spanOnDay, type PlacedSpan, type Span } from '@/logic/dayGrid'
import { dateLine } from '@/logic/labels'
import type { PlanItem } from '@/logic/plan'
import type { Now } from '@/logic/status'
import { useCalendar } from '@/store/calendar'
import { useCategories } from '@/store/categories'
import { openEventCard } from '@/store/ui'
import type { DateStr } from '@/types'
import { useSheen } from './useSheen'
import './HourGrid.css'

/** Pixels an hour; matches --hour in HourGrid.css. */
const HOUR = 54
/** A task due at a time takes an hour from it, as in Week, so its title stays readable. */
const TASK_MINUTES = 60
/** The shortest block drawn, so a quick event still shows its title. */
const MIN_MINUTES = 30
/** Below this height a block puts its title and time on one line. */
const COMPACT_PX = 40

interface HourGridProps {
  date: DateStr
  now: Now
  /** The day's tasks that keep a time on it. */
  tasks: PlanItem[]
  /** The day's events, all-day and timed. */
  events: ShownEvent[]
  people: People
}

/**
 * The Day view's hours, at full width: 07:00 to 23:00, stretched for anything
 * earlier or later, with the day's all-day events in a row above. Events are
 * filled with their owner's colour and open their card; a task due at a time
 * is outlined in its category colour, its ring ticks, and a long press drags
 * it to another day in the strip. Tapping an empty hour does nothing. Today
 * shows a red line at the current time and opens scrolled to it; other days
 * open at 08:00.
 */
export function HourGrid({ date, now, tasks, events, people }: HourGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const calendars = useCalendar((s) => s.calendars)
  const allDay = useMemo(() => events.filter((e) => e.allDay), [events])

  const layout = useMemo(() => {
    const timed = new Map<string, ShownEvent>()
    const timedTasks = new Map<string, PlanItem>()
    const spans: Span[] = []
    for (const ev of events) {
      if (ev.allDay) continue
      const span = spanOnDay(ev.start, ev.end, date)
      if (!span) continue
      const key = `ev:${ev.calendarId}:${ev.id}`
      timed.set(key, ev)
      spans.push({ key, ...span })
    }
    for (const item of tasks) {
      if (!item.time) continue
      const key = `task:${item.key}`
      const start = minutesOf(item.time)
      timedTasks.set(key, item)
      spans.push({ key, start, end: Math.min(24 * 60, start + TASK_MINUTES) })
    }
    const placed = layoutSpans(spans, MIN_MINUTES)
    return { placed, timed, timedTasks, hours: gridHours(placed) }
  }, [events, tasks, date])

  const { from, to } = layout.hours
  const isToday = date === now.date
  const nowMin = minutesOf(now.time)
  const showNow = isToday && nowMin >= from * 60 && nowMin <= to * 60

  // each day opens on a whole hour: today the hour before the current one, any other day 08:00
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const hour = isToday ? Math.floor(nowMin / 60) - 1 : 8
    el.scrollTop = Math.max(0, (hour - from) * HOUR)
  }, [date]) // eslint-disable-line react-hooks/exhaustive-deps

  const heightOf = (p: PlacedSpan) => ((p.end - p.start) / 60) * HOUR - 2
  const box = (p: PlacedSpan): React.CSSProperties => ({
    top: ((p.start - from * 60) / 60) * HOUR,
    height: heightOf(p),
    left: `calc(${p.col} * 100% / ${p.cols})`,
    width: `calc(100% / ${p.cols} - 3px)`,
  })
  const calName = (ev: ShownEvent) => calendars.find((c) => c.id === ev.calendarId)?.name ?? 'Calendar'

  const hours: number[] = []
  for (let h = from; h <= to; h += 1) hours.push(h)

  return (
    <section className="hgrid" aria-label={`${fmtDate(date)}, by the hour`}>
      {allDay.length > 0 && (
        <div className="hgrid__allday">
          <span className="hgrid__allday-label">All day</span>
          <div className="hgrid__allday-list">
            {allDay.map((ev) => (
              <button key={`${ev.calendarId}:${ev.id}`} type="button" className={`hallday ev--${eventTone(ev.owner, people)}`} onClick={() => openEventCard({ calendarId: ev.calendarId, eventId: ev.id })}>
                <OwnerMark ev={ev} people={people} />
                <span className="hallday__title">{ev.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* focusable, so the hours scroll from the keyboard on a day with nothing in them to tab to */}
      <div className="hgrid__scroll" ref={scrollRef} tabIndex={0}>
        <div className="hgrid__body" style={{ height: (to - from) * HOUR }}>
          {hours.map((h) => (
            <div key={h} className="hgrid__hour" style={{ top: (h - from) * HOUR }} aria-hidden="true">
              {h < 24 && <span className="hgrid__label">{pad2(h)}:00</span>}
            </div>
          ))}
          <div className="hgrid__lane">
            {layout.placed.map((p) => {
              const ev = layout.timed.get(p.key)
              if (ev) return <EventBlock key={p.key} ev={ev} style={box(p)} compact={heightOf(p) < COMPACT_PX} whose={whoseCalendar(ev, people, calName(ev))} people={people} />
              const item = layout.timedTasks.get(p.key)
              return item ? <TaskBlock key={p.key} item={item} date={date} now={now} style={box(p)} /> : null
            })}
          </div>
          {showNow && (
            <div className="hgrid__now" style={{ top: ((nowMin - from * 60) / 60) * HOUR }} aria-hidden="true">
              <span className="hgrid__now-dot" />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function EventBlock({ ev, style, compact, whose, people }: { ev: ShownEvent; style: React.CSSProperties; compact: boolean; whose: string; people: People }) {
  const when = `${ev.startTime} – ${ev.endTime}`
  return (
    <button
      type="button"
      className={`hblock hblock--event ev--${eventTone(ev.owner, people)} ${compact ? 'hblock--compact' : ''}`}
      style={style}
      aria-label={`${ev.title}, ${when}, ${whose}`}
      onClick={() => openEventCard({ calendarId: ev.calendarId, eventId: ev.id })}
    >
      <span className="hblock__text">
        <span className="hblock__title">{ev.title}</span>
        <span className="hblock__meta">
          {when} · {whose}
        </span>
      </span>
      {!compact && <OwnerMark ev={ev} people={people} />}
    </button>
  )
}

/** A task kept at a time: the block opens it, the ring ticks it, a long press lifts it. */
function TaskBlock({ item, date, now, style }: { item: PlanItem; date: DateStr; now: Now; style: React.CSSProperties }) {
  const navigate = useNavigate()
  const actions = useTaskActions()
  const category = useCategories((s) => (item.task.categoryId ? (s.byId[item.task.categoryId] ?? null) : null))
  const t = item.task
  const color = category ? `var(--${category.color})` : null
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({ id: item.key, data: { item }, disabled: item.dueMark })
  const ref = useRef<HTMLDivElement | null>(null)
  const setRefs = useCallback(
    (el: HTMLDivElement | null) => {
      ref.current = el
      setNodeRef(el)
    },
    [setNodeRef],
  )
  useSheen(ref, t.id)
  const line = dateLine(t, { day: date }, now)
  const cat = color ? ({ '--cat': color } as React.CSSProperties) : undefined
  return (
    <div
      ref={setRefs}
      className={`hblock hblock--task ${isDragging ? 'hblock--lifted' : ''}`}
      style={{ ...style, ...cat }}
      data-task-id={t.id}
      onContextMenu={(e) => e.preventDefault()}
      {...listeners}
    >
      <button type="button" className="hblock__open" aria-label={`Open ${t.title}`} onClick={() => navigate(`/task/${t.id}`)} />
      <StatusRing state={t.waiting ? 'waiting' : 'open'} color={color} size="small" label={`Complete ${t.title}`} onToggle={() => actions.toggleDone(t)} />
      <span className="hblock__text">
        <span className="hblock__title">{t.title}</span>
        <span className={`hblock__meta ${line?.tone ? `hblock__meta--${line.tone}` : ''}`}>{line?.text ?? item.time}</span>
      </span>
      {/* the whole block drags by touch and mouse; this unseen button is the keyboard's way in */}
      <button type="button" ref={setActivatorNodeRef} className="hblock__handle" aria-label={`Move ${t.title} to another day`} {...attributes} />
    </div>
  )
}
